'use client';
import { useEffect } from 'react';
import { Box, Typography, Button, Container, Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, LinearProgress } from '@mui/material';
import { TrendingUp, CheckCircle, Group, Assessment, Speed, Shield, People, Analytics } from '@mui/icons-material';
import Header from '../components/HomepageContent/Header';
import Footer from '../components/HomepageContent/Footer';
import Link from 'next/link';

export default function Outcomes() {
  useEffect(() => {
    document.title = 'CareAscendium - Outcomes';
  }, []);
  const processOutcomes = [
    { metric: "Documentation Time", before: "15 hours/week", after: "5 hours/week", improvement: "67% reduction", efficiency: 67 },
    { metric: "Audit Preparation", before: "8 hours/month", after: "2 hours/month", improvement: "75% reduction", efficiency: 75 },
    { metric: "Incident Reporting", before: "45 minutes", after: "15 minutes", improvement: "67% faster", efficiency: 67 },
    { metric: "Care Plan Updates", before: "3 hours/week", after: "1 hour/week", improvement: "67% reduction", efficiency: 67 },
    { metric: "Compliance Monitoring", before: "Manual tracking", after: "Automated alerts", improvement: "100% coverage", efficiency: 100 }
  ];

  const complianceOutcomes = [
    { metric: "CMS Citations", before: "3-5 per year", after: "0-1 per year", improvement: "80% reduction", efficiency: 80 },
    { metric: "F-Tag Compliance", before: "85%", after: "98%", improvement: "13% increase", efficiency: 98 },
    { metric: "Survey Readiness", before: "Reactive", after: "Proactive", improvement: "Continuous monitoring", efficiency: 100 },
    { metric: "Regulatory Updates", before: "Manual tracking", after: "Automated notifications", improvement: "100% accuracy", efficiency: 100 },
    { metric: "Documentation Accuracy", before: "90%", after: "99%", improvement: "9% increase", efficiency: 99 }
  ];

  const engagementOutcomes = [
    { metric: "Staff Satisfaction", before: "6.5/10", after: "8.5/10", improvement: "31% increase", efficiency: 85 },
    { metric: "Resident Satisfaction", before: "7.2/10", after: "8.8/10", improvement: "22% increase", efficiency: 88 },
    { metric: "Family Communication", before: "Monthly updates", after: "Real-time access", improvement: "100% transparency", efficiency: 100 },
    { metric: "Care Team Collaboration", before: "Siloed departments", after: "Integrated workflows", improvement: "Seamless coordination", efficiency: 100 },
    { metric: "Leadership Visibility", before: "Weekly reports", after: "Real-time dashboards", improvement: "Instant insights", efficiency: 100 }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      {/* Top Navigation Bar */}
      <Header />

      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 50%, #2A5A9C 100%)',
        py: 12, 
        px: 4,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <Assessment sx={{ fontSize: 64, color: 'white', mr: 3 }} />
            <Typography 
              variant="h1" 
              sx={{ 
                color: 'white', 
                mb: 4,
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Measurable Outcomes
            </Typography>
          </Box>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            CareAscendium delivers quantifiable improvements across all aspects of skilled nursing facility operations
          </Typography>
        </Container>
      </Box>

      {/* Key Metrics Overview */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(0,85,164,0.1)'
              }}
            >
              <Speed sx={{ fontSize: 48, color: '#153C7F', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#153C7F', fontWeight: 700, mb: 1 }}>
                67%
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Average Process Efficiency
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(0,85,164,0.1)'
              }}
            >
              <Shield sx={{ fontSize: 48, color: '#28a745', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#28a745', fontWeight: 700, mb: 1 }}>
                98%
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Compliance Rate
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(0,85,164,0.1)'
              }}
            >
              <People sx={{ fontSize: 48, color: '#ff6b35', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#ff6b35', fontWeight: 700, mb: 1 }}>
                85%
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Staff Satisfaction
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(0,85,164,0.1)'
              }}
            >
              <Analytics sx={{ fontSize: 48, color: '#9c27b0', mb: 2 }} />
              <Typography variant="h3" sx={{ color: '#9c27b0', fontWeight: 700, mb: 1 }}>
                100%
              </Typography>
              <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>
                Real-time Visibility
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Process Outcomes Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <Speed sx={{ fontSize: 48, color: '#153C7F', mr: 2 }} />
            <Typography variant="h2" sx={{ color: '#153C7F', fontWeight: 700 }}>
              Process Outcomes
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
            Streamlined workflows and automated processes that save time and reduce administrative burden
          </Typography>
        </Box>

        <Paper 
          elevation={8}
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(0,85,164,0.1)'
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                                  <TableRow sx={{ 
                    background: 'linear-gradient(45deg, #153C7F, #1E4A8C)',
                    '& th': { 
                      color: 'white', 
                      fontWeight: 700,
                      fontSize: '1rem',
                      padding: 3
                    }
                  }}>
                  <TableCell>Metric</TableCell>
                  <TableCell align="center">Before CareAscendium</TableCell>
                  <TableCell align="center">After CareAscendium</TableCell>
                  <TableCell align="center">Improvement</TableCell>
                  <TableCell align="center">Efficiency Gain</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processOutcomes.map((outcome, index) => (
                  <TableRow 
                    key={index} 
                    sx={{ 
                      '&:nth-of-type(odd)': { bgcolor: '#f8f9fa' },
                      '&:hover': { bgcolor: '#e3f2fd' },
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {outcome.metric}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#666' }}>
                      {outcome.before}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#28a745', fontWeight: 600 }}>
                      {outcome.after}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#153C7F', fontWeight: 700 }}>
                      {outcome.improvement}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={outcome.efficiency} 
                          sx={{ 
                            width: 60, 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: '#153C7F'
                            }
                          }} 
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#153C7F' }}>
                          {outcome.efficiency}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      {/* Compliance Outcomes Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        py: 10 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
              <Shield sx={{ fontSize: 48, color: '#153C7F', mr: 2 }} />
              <Typography variant="h2" sx={{ color: '#153C7F', fontWeight: 700 }}>
                Compliance Outcomes
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
              Enhanced regulatory compliance and reduced risk through automated monitoring and tracking
            </Typography>
          </Box>

          <Paper 
            elevation={8}
            sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(0,85,164,0.1)'
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    background: 'linear-gradient(45deg, #153C7F, #1E4A8C)',
                    '& th': { 
                      color: 'white', 
                      fontWeight: 700,
                      fontSize: '1rem',
                      padding: 3
                    }
                  }}>
                    <TableCell>Metric</TableCell>
                    <TableCell align="center">Before CareAscendium</TableCell>
                    <TableCell align="center">After CareAscendium</TableCell>
                    <TableCell align="center">Improvement</TableCell>
                    <TableCell align="center">Compliance Level</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {complianceOutcomes.map((outcome, index) => (
                    <TableRow 
                      key={index} 
                      sx={{ 
                        '&:nth-of-type(odd)': { bgcolor: '#f8f9fa' },
                        '&:hover': { bgcolor: '#e3f2fd' },
                        transition: 'background-color 0.3s ease'
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600, fontSize: '1rem' }}>
                        {outcome.metric}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#666' }}>
                        {outcome.before}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#28a745', fontWeight: 600 }}>
                        {outcome.after}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#153C7F', fontWeight: 700 }}>
                        {outcome.improvement}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={outcome.efficiency} 
                            sx={{ 
                              width: 60, 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: '#28a745'
                              }
                            }} 
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#28a745' }}>
                            {outcome.efficiency}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Container>
      </Box>

      {/* Engagement & Culture Outcomes Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <People sx={{ fontSize: 48, color: '#153C7F', mr: 2 }} />
            <Typography variant="h2" sx={{ color: '#153C7F', fontWeight: 700 }}>
              Engagement & Culture Outcomes
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
            Improved satisfaction and collaboration across all stakeholders in the care ecosystem
          </Typography>
        </Box>

        <Paper 
          elevation={8}
          sx={{ 
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(0,85,164,0.1)'
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: 'linear-gradient(45deg, #153C7F, #1E4A8C)',
                  '& th': { 
                    color: 'white', 
                    fontWeight: 700,
                    fontSize: '1rem',
                    padding: 3
                  }
                }}>
                  <TableCell>Metric</TableCell>
                  <TableCell align="center">Before CareAscendium</TableCell>
                  <TableCell align="center">After CareAscendium</TableCell>
                  <TableCell align="center">Improvement</TableCell>
                  <TableCell align="center">Satisfaction Level</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {engagementOutcomes.map((outcome, index) => (
                  <TableRow 
                    key={index} 
                    sx={{ 
                      '&:nth-of-type(odd)': { bgcolor: '#f8f9fa' },
                      '&:hover': { bgcolor: '#e3f2fd' },
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, fontSize: '1rem' }}>
                      {outcome.metric}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#666' }}>
                      {outcome.before}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#28a745', fontWeight: 600 }}>
                      {outcome.after}
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#153C7F', fontWeight: 700 }}>
                      {outcome.improvement}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={outcome.efficiency} 
                          sx={{ 
                            width: 60, 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: '#ff6b35'
                            }
                          }} 
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff6b35' }}>
                          {outcome.efficiency}%
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      {/* Measurement Approach Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        py: 10 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
              Our Measurement Approach
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
              We use a comprehensive methodology to track and validate outcomes across all facility operations
            </Typography>
          </Box>

          <Grid container spacing={6}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper 
                elevation={6}
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid rgba(0,85,164,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <Assessment sx={{ fontSize: 64, color: '#153C7F', mb: 3 }} />
                <Typography variant="h4" sx={{ color: '#153C7F', mb: 3, fontWeight: 700 }}>
                  Baseline Assessment
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.8, fontSize: '1.1rem' }}>
                  Comprehensive evaluation of current processes, compliance status, and performance metrics before implementation
                </Typography>
              </Paper>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper 
                elevation={6}
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid rgba(0,85,164,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <Analytics sx={{ fontSize: 64, color: '#28a745', mb: 3 }} />
                <Typography variant="h4" sx={{ color: '#153C7F', mb: 3, fontWeight: 700 }}>
                  Continuous Monitoring
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.8, fontSize: '1.1rem' }}>
                  Real-time tracking of key performance indicators and automated reporting for ongoing measurement
                </Typography>
              </Paper>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper 
                elevation={6}
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  textAlign: 'center',
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid rgba(0,85,164,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }
                }}
              >
                <CheckCircle sx={{ fontSize: 64, color: '#ff6b35', mb: 3 }} />
                <Typography variant="h4" sx={{ color: '#153C7F', mb: 3, fontWeight: 700 }}>
                  Validation & Reporting
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.8, fontSize: '1.1rem' }}>
                  Regular validation of outcomes through independent audits and detailed reporting to stakeholders
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ 
        py: 12, 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 100%)',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ mb: 4, fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' } }}>
            Achieve These Outcomes
          </Typography>
          <Typography variant="h5" sx={{ mb: 6, opacity: 0.9, lineHeight: 1.6 }}>
            Join facilities already experiencing these measurable improvements with CareAscendium
          </Typography>
          <Link href="/contact" style={{ textDecoration: 'none' }}>
            <Button 
              variant="contained" 
              size="large"
              startIcon={<TrendingUp />}
              sx={{ 
                bgcolor: 'white',
                color: '#153C7F',
                px: 6,
                py: 3,
                fontSize: '1.2rem',
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(255,255,255,0.3)',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  boxShadow: '0 6px 20px rgba(255,255,255,0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Start Your Transformation
            </Button>
          </Link>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
} 